import { BLOG_RECOMMENDED_DESTINATIONS_MAX, BLOG_RECOMMENDED_POSTS_MAX } from '@/lib/blogConstants';
import { getSiteConfig } from '@/config/sites';

/** @param {unknown} value Prisma Json */
export function parseJsonIdArray(value) {
  if (value == null) {
    return [];
  }
  if (Array.isArray(value)) {
    return value
      .filter((x) => typeof x === 'string' && x.length > 0)
      .map((x) => x.trim())
      .filter(Boolean);
  }
  return [];
}

/**
 * Препоръчани корици от JSON колоната (legacy: string[] destinationId;
 * нов формат: { destinationId, templateId }[]).
 * @param {unknown} value
 * @returns {{ destinationId: string; templateId: number | null }[]}
 */
export function parseJsonCoverRefs(value) {
  if (value == null || !Array.isArray(value)) {
    return [];
  }
  const out = [];
  const seen = new Set();

  for (const item of value) {
    let ref = null;

    if (typeof item === 'string' && item.trim()) {
      ref = { destinationId: item.trim(), templateId: null };
    } else if (item && typeof item === 'object' && !Array.isArray(item)) {
      const destinationId = String(item.destinationId || '').trim();
      if (!destinationId) {
        continue;
      }
      let templateId = null;
      if (item.templateId !== undefined && item.templateId !== null && item.templateId !== '') {
        const n = Number(item.templateId);
        if (Number.isFinite(n)) {
          templateId = n;
        }
      }
      ref = { destinationId, templateId };
    }

    if (!ref) {
      continue;
    }

    const key = `${ref.destinationId}:${ref.templateId ?? 'first'}`;
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    out.push(ref);
  }

  return out;
}

/** @param {unknown} input */
export function normalizeRecommendedPostIds(input) {
  const arr = Array.isArray(input) ? input : [];
  const ids = [...new Set(arr.map((x) => String(x || '').trim()).filter(Boolean))];
  return ids.slice(0, BLOG_RECOMMENDED_POSTS_MAX);
}

/**
 * @param {unknown} input
 * @returns {{ destinationId: string; templateId: number | null }[]}
 */
export function normalizeRecommendedCoverRefs(input) {
  const parsed = parseJsonCoverRefs(input);
  return parsed.slice(0, BLOG_RECOMMENDED_DESTINATIONS_MAX);
}

/** @deprecated използвай normalizeRecommendedCoverRefs */
export function normalizeRecommendedDestinationIds(input) {
  return normalizeRecommendedCoverRefs(input);
}

/**
 * @param {object} prisma Prisma client
 * @param {{
 *   postIds: string[];
 *   coverRefs?: { destinationId: string; templateId: number | null }[];
 *   destinationIds?: string[];
 *   excludePostId?: string | null;
 * }} opts
 * @returns {Promise<string | null>}
 */
export async function validateRecommendedRefs(
  prisma,
  { postIds, coverRefs, destinationIds, excludePostId },
) {
  const postIdsClean = postIds.filter((pid) => pid && pid !== excludePostId);
  if (postIdsClean.length) {
    const n = await prisma.blogPost.count({ where: { id: { in: postIdsClean } } });
    if (n !== postIdsClean.length) {
      return 'Невалидни препоръчани статии';
    }
  }

  const refs =
    coverRefs ??
    (destinationIds?.length
      ? destinationIds.map((id) => ({ destinationId: id, templateId: null }))
      : []);

  if (!refs.length) {
    return null;
  }

  const destIds = [...new Set(refs.map((r) => r.destinationId))];
  const destinations = await prisma.destination.findMany({
    where: { id: { in: destIds }, isActive: true },
    include: { templates: { select: { templateId: true } } },
  });
  const destMap = new Map(destinations.map((d) => [d.id, d]));

  for (const ref of refs) {
    const dest = destMap.get(ref.destinationId);
    if (!dest) {
      return `Невалидни ${getSiteConfig().content.taxonomyNounPlural} или корици`;
    }
    if (ref.templateId != null) {
      const hasTemplate = dest.templates.some((t) => t.templateId === ref.templateId);
      if (!hasTemplate) {
        return `Невалидна корица за избраната ${getSiteConfig().content.taxonomyNoun}`;
      }
    }
  }

  return null;
}
